import { useCallback } from 'react';
import BarChartScenario from '../bar-chart-scenario/BarChartScenario';
import { BarChartByProps } from '../config';
import { useTranslation } from 'react-i18next';
import { ScenarioDataSetLabelV2, ScenarioQueryParams } from '@customer/store/apis/scenariosApi';
import { getVehicleInteractionType } from '@customer/shared/utils/translationHelpers';
import { VICategory, VIType } from '@customer/store/apis/interactionsApi';
import { GetTooltipTitle } from '../utils';
import { CustomBarChartLabel, OnCategorySelect } from '../bar-select/BarSelectPlugin';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { saveFilters, setVehicleInteractionTypes } from '@customer/store/slices/filtersSlice';
import { isEnumValue } from '@cas/utils/typeHelpers';

export const BarChartByVehicleInteractionType = <T extends ScenarioQueryParams>(
  props: BarChartByProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const siteId = useAppSelector(state => state.auth.siteId);

  const getVehicleInteractionTypeLabel = useCallback(
    ({ name }: ScenarioDataSetLabelV2) => {
      // If the interactionType is a VICategory enum value, return the translated label
      return isEnumValue(name, VICategory) ? getVehicleInteractionType(t, name) : name;
    },
    [t],
  );

  const getTooltipTitle = useCallback<GetTooltipTitle>(
    (_, categoryLabelKey, datasetLabelKey) => {
      // Combine the category label and dataSet label to get the translation for the vehicle interaction type.
      return t(`vi.name.${categoryLabelKey}${datasetLabelKey}`);
    },
    [t],
  );

  const onCategorySelect = useCallback<OnCategorySelect>(
    ({ name }: CustomBarChartLabel): void => {
      if (siteId && isEnumValue(name, VICategory)) {
        const typesInCategory: VIType[] = Object.values(VIType).filter((type: VIType) =>
          type.startsWith(name),
        );

        dispatch(setVehicleInteractionTypes(typesInCategory));
        dispatch(saveFilters(siteId));
      }
    },
    [dispatch, siteId],
  );

  // const onBarSelect = useCallback<OnBarSelect>(({ name }: CustomBarChartLabel, datasetId: string): void => {
  //   if (siteId && isEnumValue(name, VICategory) && isEnumValue(datasetId, VILevel)) {
  //     const selection = `${name}${datasetId}`;

  //     if (isEnumValue(selection, VIType)) {
  //       dispatch(setVehicleInteractionTypes([selection]));
  //       dispatch(saveFilters(siteId));
  //     }
  //   }
  // }, [dispatch, siteId]);

  return (
    <BarChartScenario<T>
      {...props}
      getCategoryLabel={getVehicleInteractionTypeLabel}
      getTooltipTitle={getTooltipTitle}
      onCategorySelect={onCategorySelect}
      // Temporarily use the same function for onBarSelect until the UX is improved.
      onBarSelect={onCategorySelect}
      stacked={false}
    />
  );
};

export default BarChartByVehicleInteractionType;
